import React from "react";

export default function PrivacyPolicy() {
  return (
    <div className="flex items-center justify-center">
      <div className="max-w-[1200px] px-4 md:py-16 py-8 text-gray-800 ">
      <h1 className="text-4xl font-bold mb-10 font-montserrat">
        Privacy Policy
      </h1>

      <p className="mb-10 font-roboto">
        Think41 Technologies Private Limited (“Think41,” “we,” “our,” or “us”)
        respects your privacy and is committed to protecting your personal data.
        This Privacy Policy explains how we collect, use, and protect your
        information when you interact with our websites, products, and services.
      </p>

      <Section title="1. Who We Are">
        <p>
          Think41 is a technology company offering conversational AI, automation
          tools, and enterprise software solutions. Our offerings are designed
          for global clients and align with applicable data privacy regulations
          including the General Data Protection Regulation (GDPR), California
          Consumer Privacy Act (CCPA), and the EU-U.S. Data Privacy Framework
          (DPF) (as applicable).
        </p>
      </Section>

      <Section title="2. Scope">
        <p>This Privacy Policy applies to data collected via our:</p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>Website(s)</li>
          <li>Conversational AI agents and platforms</li>
          <li>Product interfaces</li>
          <li>Email and support interactions</li>
        </ul>
        <p className="mt-2">
          It also covers offline contact if it leads to data processing. For
          data processed on behalf of our enterprise clients (as processors),
          their respective privacy policies apply.
        </p>
      </Section>

      <Section title="3. What We Collect">
        <SubSection title="a. Automatically Collected Data">
          <ul className="list-disc list-inside space-y-1">
            <li>IP address</li>
            <li>Device and browser type</li>
            <li>Operating system</li>
            <li>Usage patterns and timestamps</li>
            <li>Referrer URLs</li>
            <li>Analytics data (via cookies or similar tools)</li>
          </ul>
        </SubSection>
        <SubSection title="b. Voluntarily Shared Information">
          <ul className="list-disc list-inside space-y-1">
            <li>Name and email address</li>
            <li>Phone number</li>
            <li>Company information</li>
            <li>User inputs to our AI systems</li>
            <li>Feedback and support queries</li>
          </ul>
        </SubSection>
      </Section>

      <Section title="4. How We Use Your Information">
        <ul className="list-disc list-inside space-y-1">
          <li>Provide and improve our products and services</li>
          <li>Respond to your queries or requests</li>
          <li>Customize your experience with our platforms</li>
          <li>Send updates, announcements, and offers (with your consent)</li>
          <li>Analyze platform performance and user trends</li>
          <li>Ensure platform security and prevent misuse</li>
          <li>Fulfill legal obligations</li>
        </ul>
      </Section>

      <Section title="5. Who Can Access Your Data">
        <p>
          Only authorized Think41 personnel or partners who require access to
          perform specific tasks—such as developers, customer support, or
          marketing—may access your data. Access is role-based and limited by
          necessity.
        </p>
      </Section>

      <Section title="6. Sharing Your Information">
        <p>We may share your data with:</p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>
            Trusted Service Providers: For hosting, analytics, communication,
            and infrastructure
          </li>
          <li>
            Legal Authorities: When required to comply with laws or regulations
          </li>
          <li>
            In Business Transfers: If Think41 is acquired or merged with another
            entity
          </li>
          <li>
            Client Organizations: If you're interacting with our platform as
            part of a client’s service (in which case, the client is the data
            controller)
          </li>
        </ul>
        <p className="mt-2">We do not sell your data.</p>
      </Section>

      <Section title="7. Data Retention">
        <p>We retain personal data only as long as necessary for:</p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>Providing services</li>
          <li>Complying with legal, contractual, or operational obligations</li>
          <li>Auditing and analytics</li>
        </ul>
        <p className="mt-2">
          We securely delete or anonymize data once retention purposes are
          fulfilled.
        </p>
      </Section>

      <Section title="8. Your Rights">
        <SubSection title="Under GDPR (EU/UK residents):">
          <ul className="list-disc list-inside space-y-1">
            <li>Access your data</li>
            <li>Correct inaccuracies</li>
            <li>Delete your data</li>
            <li>Restrict or object to processing</li>
            <li>Port your data to another provider</li>
            <li>Withdraw consent at any time</li>
          </ul>
        </SubSection>
        <SubSection title="Under CCPA (California residents):">
          <ul className="list-disc list-inside space-y-1">
            <li>Request information about data collected</li>
            <li>Request deletion of your personal data</li>
            <li>Opt out of data sharing (we do not sell personal data)</li>
            <li>Exercise rights without discrimination</li>
          </ul>
        </SubSection>
        <p className="mt-2">
          To exercise these rights, contact us at:{" "}
          <a
            href="mailto:contact@think41.com"
            className="text-blue-600 underline"
          >
            contact@think41.com
          </a>
        </p>
      </Section>

      <Section title="9. Cookies and Tracking">
        <p>
          We use cookies and similar technologies to enhance site functionality
          and understand user behavior. You can control cookie settings via your
          browser or opt-out mechanisms where available.
        </p>
      </Section>

      <Section title="10. Security">
        <p>
          We implement robust technical and organizational measures to protect
          your personal information against unauthorized access, disclosure,
          alteration, or destruction.
        </p>
      </Section>

      <Section title="11. Children’s Privacy">
        <p>
          Our services are not intended for individuals under the age of 16. We
          do not knowingly collect personal data from children. If we become
          aware of such collection, we will take appropriate action to delete
          the data.
        </p>
      </Section>

      <Section title="12. International Data Transfers">
        <p>
          Where applicable, Think41 adheres to international data protection
          principles and may transfer data across borders under lawful
          mechanisms, such as Standard Contractual Clauses (SCCs) or
          participation in recognized frameworks like the EU-U.S. Data Privacy
          Framework (if certified).
        </p>
      </Section>

      <Section title="13. Third-Party Links">
        <p>
          Our platforms may link to external websites or services. We are not
          responsible for their privacy practices. Please review their privacy
          policies before interacting.
        </p>
      </Section>

      <Section title="14. Updates to This Policy">
        <p>
          We may update this Privacy Policy to reflect changes in legal
          requirements or our practices. Significant changes will be
          communicated clearly via our website or other appropriate channels.
        </p>
      </Section>

      <Section title="15. Contact Us">
        <p>
          For questions, requests, or concerns regarding your personal data,
          please reach out to:{" "}
          <a
            href="mailto:contact@think41.com"
            className="text-blue-600 underline"
          >
            contact@think41.com
          </a>
        </p>
        <p><span className=" text-t-blue-1 font-semibold">Last Updated:</span> 28th May 2025</p>
        <a href="https://www.google.com/maps/place/Think41/@12.9157289,77.6325263,17z/data=!3m1!4b1!4m6!3m5!1s0x3bae15e4ab29f6a9:0x78164c186207e91e!8m2!3d12.9157289!4d77.6325263!16s%2Fg%2F11w2dlq4h4?entry=ttu&g_ep=EgoyMDI1MDUyMS4wIKXMDSoASAFQAw%3D%3D" target="_blank" className="mt-2 hover:underline cursor-pointer">
          Think41 Technologies Private Limited
          <br />
          2nd Floor, Obeya Gusto, HSR Layout
          <br />
          Bengaluru, Karnataka 560102
        </a>
      </Section>
    </div>
    </div>

  );
}

function Section({ title, children }) {
  return (
    <section className="mb-14">
      <h2 className="text-2xl font-semibold mb-4 font-montserrat">{title}</h2>
      <div className="space-y-2 font-roboto">{children}</div>
    </section>
  );
}

function SubSection({ title, children }) {
  return (
    <div className="mb-6">
      <h3 className="text-lg font-medium mb-2 font-montserrat">{title}</h3>
      <div className="space-y-1 font-roboto">{children}</div>
    </div>
  );
}
